import { Injectable } from "@angular/core"
import { ApiService } from "../api.service"
import { ParentClass } from "../../parent"
import { StreamContentParameters } from "@common-lib/interfaces/api/content.api"
import { TokenService } from "@services/token.service"
import { Misc } from "@common-lib/modules/misc"
import { StreamingBufferV2Metadata } from "@common-lib/interfaces/api/sockets"
import { CurrentSongAudioBuffer } from "@services/audio/player.store"

type StreamCompletion = "cancelled" | "end" | "inactivity"

interface ActiveStream {
    controller: AbortController
    cancel: () => void
}

interface StreamAttemptResult {
    completion: StreamCompletion
    buffers: StreamingBufferV2Metadata[]
}

@Injectable()
export class StreamingService extends ParentClass {
    /** A lost socket end event gets a short grace period after HTTP completion. */
    private static readonly END_EVENT_GRACE_TIMEOUT_MS = 750
    private static readonly INITIAL_RESPONSE_TIMEOUT_MS = 30_000
    private static readonly MAX_RETRIES = 3

    private activeStream: ActiveStream | undefined
    private lastRequestTimestamp = 0
    private streamGeneration = 0

    constructor(
        private apiService: ApiService,
        private tokenService: TokenService
    ) {
        super()
    }

    /**
     * Stops both phases of the current request: the fetch and the socket wait.
     * Cancellation is an expected lifecycle event and resolves streamContent with
     * undefined rather than surfacing an AbortError to an unawaited UI action.
     */
    public cancelCurrentStream(): void {
        this.streamGeneration++
        const activeStream = this.activeStream
        if (activeStream === undefined) {
            return
        }

        // Settle the socket wait before aborting fetch so its rejection is handled
        // by the attempt that installed it.
        activeStream.cancel()
        activeStream.controller.abort()

        if (this.activeStream === activeStream) {
            this.activeStream = undefined
        }
    }

    public ngOnDestroy(): void {
        this.cancelCurrentStream()
        super.ngOnDestroy()
    }

    public async streamContent(
        trackID: string,
        onReceiveBuffer: (value: StreamingBufferV2Metadata) => Promise<void>,
        retries = 10
    ): Promise<CurrentSongAudioBuffer[] | undefined> {
        this.cancelCurrentStream()
        const streamGeneration = this.streamGeneration

        const retryCount = Math.max(
            0,
            Math.min(StreamingService.MAX_RETRIES, retries)
        )

        for (let attempt = 0; attempt <= retryCount; attempt++) {
            if (streamGeneration !== this.streamGeneration) {
                return undefined
            }

            try {
                const result = await this.streamAttempt(
                    trackID,
                    onReceiveBuffer
                )

                if (result.completion === "cancelled") {
                    return undefined
                }

                return result.buffers
                    .sort((a, b) => a.from - b.from)
                    .map(value => ({
                        start: value.from,
                        end: value.to,
                        buffer: value.audio,
                    }))
            } catch (error) {
                if (this.isAbortError(error)) {
                    return undefined
                }

                if (attempt === retryCount) {
                    return undefined
                }

                await Misc.wait(Math.pow(2, attempt) / 10)

                if (streamGeneration !== this.streamGeneration) {
                    return undefined
                }
            }
        }

        return undefined
    }

    private streamAttempt(
        trackID: string,
        onReceiveBuffer: (value: StreamingBufferV2Metadata) => Promise<void>
    ): Promise<StreamAttemptResult> {
        const apiRoute = "/content/stream/v2"
        const controller = new AbortController()
        const requestTimestamp = this.nextRequestTimestamp()
        const params: StreamContentParameters = {
            trackID,
            requestTimestamp,
        }
        const socket = this.apiService.socket.value
        const buffers: StreamingBufferV2Metadata[] = []

        let settled = false
        let receivedBuffer = false
        let fetchCompleted = false
        let inactivityTimer: ReturnType<typeof setTimeout> | undefined
        let bufferProcessing = Promise.resolve()

        return new Promise<StreamAttemptResult>((resolve, reject) => {
            let finishing = false

            const cleanup = () => {
                if (inactivityTimer !== undefined) {
                    clearTimeout(inactivityTimer)
                    inactivityTimer = undefined
                }
                socket.off("streamAudioBuffer", bufferListener)
                socket.off("streamAudioEnd", endListener)
                if (this.activeStream?.controller === controller) {
                    this.activeStream = undefined
                }
            }

            const finish = (completion: StreamCompletion) => {
                if (settled || finishing) {
                    return
                }
                finishing = true

                bufferProcessing
                    .then(() => {
                        if (settled) {
                            return
                        }
                        settled = true
                        if (!controller.signal.aborted) {
                            controller.abort()
                        }
                        cleanup()
                        resolve({ completion, buffers })
                    })
                    .catch(fail)
            }

            const fail = (
                error: unknown = new Error("Audio stream failed")
            ) => {
                if (settled) {
                    return
                }
                settled = true
                if (!controller.signal.aborted) {
                    controller.abort()
                }
                cleanup()
                reject(error)
            }

            const cancel = () => {
                if (settled) {
                    return
                }
                settled = true
                cleanup()
                resolve({ completion: "cancelled", buffers })
            }

            const scheduleTerminalTimer = () => {
                if (inactivityTimer !== undefined) {
                    clearTimeout(inactivityTimer)
                }

                const timeout = fetchCompleted
                    ? StreamingService.END_EVENT_GRACE_TIMEOUT_MS
                    : StreamingService.INITIAL_RESPONSE_TIMEOUT_MS
                inactivityTimer = setTimeout(() => {
                    if (fetchCompleted && receivedBuffer) {
                        finish("inactivity")
                    } else {
                        fail(new Error("Audio stream timed out before data"))
                    }
                }, timeout)
            }

            const bufferListener = (data: StreamingBufferV2Metadata) => {
                if (
                    settled ||
                    finishing ||
                    data.requestTimestamp !== requestTimestamp
                ) {
                    return
                }

                receivedBuffer = true
                if (inactivityTimer !== undefined) {
                    clearTimeout(inactivityTimer)
                    inactivityTimer = undefined
                }
                if (fetchCompleted) {
                    scheduleTerminalTimer()
                }
                bufferProcessing = bufferProcessing.then(async () => {
                    await onReceiveBuffer(data)
                    buffers.push(data)
                })
                bufferProcessing.catch(fail)
            }

            const endListener = (data: StreamingBufferV2Metadata) => {
                if (
                    settled ||
                    finishing ||
                    data.requestTimestamp !== requestTimestamp
                ) {
                    return
                }

                finish("end")
            }

            socket.on("streamAudioBuffer", bufferListener)
            socket.on("streamAudioEnd", endListener)

            this.activeStream = {
                controller,
                cancel,
            }
            scheduleTerminalTimer()

            // Attaching both handlers here guarantees a rejected fetch is always
            // observed, even if a socket event or cancellation settles first.
            this.apiService
                .fetchRequest({
                    url: apiRoute,
                    parameters: params,
                    urlType: "primary",
                    type: "POST",
                    controller,
                    token: this.tokenService.getToken(),
                })
                .then(response => {
                    if (response.result === 0) {
                        fail()
                        return
                    }
                    fetchCompleted = true
                    scheduleTerminalTimer()
                })
                .catch(fail)
        })
    }

    private nextRequestTimestamp(): number {
        const timestamp = Math.max(Date.now(), this.lastRequestTimestamp + 1)
        this.lastRequestTimestamp = timestamp
        return timestamp
    }

    private isAbortError(error: unknown): boolean {
        return (
            error instanceof Error &&
            (error.name === "AbortError" ||
                error.message.toLowerCase().includes("abort"))
        )
    }
}
